import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function ArticleStudioNew() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ topic: "", audience: "Parents and caregivers of neurodivergent children in Canada", region: "Ontario", intent: "Educational / search traffic", keyword: "" });
  const [strategy, setStrategy] = useState(null);
  const [choice, setChoice] = useState({ title: "", subtitle: "", slug: "", primary_keyword: "", category_id: "" });

  useEffect(() => { api.get("/article-categories").then((r) => setCats(r.data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const runStrategy = async () => {
    if (!form.topic.trim()) { toast.error("Please enter a topic."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/articles/generate-strategy", form);
      setStrategy(data);
      setChoice({ title: data.titles?.[0] || "", subtitle: data.subtitles?.[0] || "", slug: data.slug || "",
        primary_keyword: data.primary_keyword || "", category_id: "" });
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createAndGenerate = async () => {
    if (!choice.title || !choice.category_id) { toast.error("Pick a title and a category."); return; }
    setBusy(true);
    try {
      const { data: art } = await api.post("/admin/articles", {
        title: choice.title, subtitle: choice.subtitle, slug: choice.slug, primary_keyword: choice.primary_keyword,
        secondary_keywords: strategy.secondary_keywords || [], category_id: choice.category_id,
        outline: strategy.outline || [], audience: form.audience, region: form.region,
      });
      toast.info("Generating your article draft…");
      await api.post(`/admin/articles/${art.id}/generate-draft`);
      // metadata + faq best-effort
      try { await api.post(`/admin/articles/${art.id}/generate-metadata`); } catch (_) {}
      try { await api.post(`/admin/articles/${art.id}/generate-faq`); } catch (_) {}
      toast.success("Draft ready!");
      navigate(`/ops/article-studio/edit/${art.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div data-testid="article-studio-new">
      <PageHeader eyebrow="Article Studio" title="New article" subtitle="Start with a topic — KidPath AI drafts a strategy, then a full article you can edit." />

      {step === 1 && (
        <div className="kid-card p-6 max-w-2xl space-y-4" data-testid="wizard-step-1">
          <div className="space-y-1.5"><Label>Topic *</Label><Input value={form.topic} onChange={set("topic")} placeholder="e.g. Ontario autism funding" data-testid="wizard-topic" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Target audience</Label><Input value={form.audience} onChange={set("audience")} /></div>
            <div className="space-y-1.5"><Label>Province / region</Label><Input value={form.region} onChange={set("region")} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Article intent</Label><Input value={form.intent} onChange={set("intent")} /></div>
            <div className="space-y-1.5"><Label>Target keyword (optional)</Label><Input value={form.keyword} onChange={set("keyword")} placeholder="auto" /></div>
          </div>
          <button onClick={runStrategy} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="wizard-generate-strategy">
            <Sparkles size={16} /> {busy ? "Thinking…" : "Generate strategy"}
          </button>
        </div>
      )}

      {step === 2 && strategy && (
        <div className="max-w-3xl space-y-5" data-testid="wizard-step-2">
          <div className="kid-card p-5 grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-kid-muted">Primary keyword</span><div className="font-semibold text-kid-navy">{strategy.primary_keyword}</div></div>
            <div><span className="text-kid-muted">Article type</span><div className="font-semibold text-kid-navy">{strategy.article_type}</div></div>
            <div className="sm:col-span-2"><span className="text-kid-muted">Secondary keywords</span><div className="text-kid-navy">{(strategy.secondary_keywords || []).join(", ")}</div></div>
          </div>

          <div className="kid-card p-5 space-y-4">
            <div className="space-y-2">
              <Label>Choose a title</Label>
              {(strategy.titles || []).map((t, i) => (
                <label key={i} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="radio" name="title" checked={choice.title === t} onChange={() => setChoice({ ...choice, title: t })} className="mt-1" data-testid={`wizard-title-${i}`} />
                  <span className="text-kid-navy">{t}</span>
                </label>
              ))}
              <Input value={choice.title} onChange={(e) => setChoice({ ...choice, title: e.target.value })} className="mt-1" data-testid="wizard-title-input" />
            </div>
            <div className="space-y-1.5"><Label>Subtitle</Label>
              <select value={choice.subtitle} onChange={(e) => setChoice({ ...choice, subtitle: e.target.value })} className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm">
                {(strategy.subtitles || []).map((s, i) => <option key={i} value={s}>{s}</option>)}
              </select>
              <Input value={choice.subtitle} onChange={(e) => setChoice({ ...choice, subtitle: e.target.value })} className="mt-1" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Category *</Label>
                <select value={choice.category_id} onChange={(e) => setChoice({ ...choice, category_id: e.target.value })} className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm" data-testid="wizard-category">
                  <option value="">Select…</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label>Slug</Label><Input value={choice.slug} onChange={(e) => setChoice({ ...choice, slug: e.target.value })} data-testid="wizard-slug" /></div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setStep(1)} className="kid-btn kid-btn-ghost h-11 px-5">Back</button>
            <button onClick={runStrategy} disabled={busy} className="kid-btn kid-btn-ghost h-11 px-4" data-testid="wizard-regenerate"><RefreshCw size={15} /> Regenerate</button>
            <button onClick={createAndGenerate} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5 ml-auto" data-testid="wizard-create-draft">
              {busy ? "Generating…" : <>Generate full article <ArrowRight size={16} /></>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
